import { gql } from 'urql';

gql`
  query getItemAuditLogExtras($itemId: ID!) {
    categories {
      id
      name
    }
    procurementVendors {
      id
      name
    }
    units {
      id
      name
    }
    vendors {
      id
      name
      legalName
    }
    warehouses {
      id
      name
    }
    viewer {
      availableItemById(id: $itemId) {
        substitutionOverrides {
          id
          substituteItem {
            id
            name
          }
        }
      }
    }
  }
`;

gql`
  query getReadOnlyItemExtras {
    warehouses {
      id
      name
    }
    procurementVendors {
      id
      name
    }
  }
`;

export const GET_UPSERT_ITEM_EXTRAS = gql`
  query getUpsertItemExtras {
    warehouses {
      id
      name
    }
    categories(filter: TOP_LEVEL) {
      id
      name
      position
      subcategories {
        id
        name
        position
      }
    }
    procurementVendors {
      id
      name
    }
    vendors {
      id
      name
      legalName
      warehouse {
        name
      }
    }
    units {
      id
      name
      abbreviation
      conversionRate
      baseUnit {
        id
        name
        conversionRate
        unitType
      }
      unitType
    }
  }
`;

export const GET_ITEM_FOR_EDIT = gql`
  query getItemForEdit($itemId: ID!, $warehouseIds: [ID!]) {
    viewer {
      availableItemById(id: $itemId) {
        __typename
        id
        name
        brandName
        brand {
          id
          name
        }
        description
        catalogStatus
        inactivatedOn
        imgUrl
        itemSize
        size {
          quantity
          unit {
            id
            name
          }
        }
        updatedAt
        vendorCode
        dimensionalFactor
        eachHeightInches
        eachLengthInches
        eachWeightLbs
        eachWidthInches
        caseHeightInches
        caseLengthInches
        caseWeightLbs
        caseWidthInches
        casesPerPallet
        casesPerLayer
        layersPerPallet
        palletWeightLbs
        dimensioned
        nmfc
        refrigerationState
        transitMethod
        hasOwnImage
        category {
          id
          name
        }
        subcategory {
          id
          name
        }
        standingOrderBehavior
        maxQuantityPerLocation
        sampleMaxQuantityPerLocation
        tags {
          badgeUrl
          displayAs
          id
          name
          parentTag {
            badgeUrl
            displayAs
            id
            name
          }
        }
        ... on InventoryItem {
          manufacturedType
          marketPrice
          perishability
          upcCodes
          locationShelfLifeDays
          shelfLifeDays
          inventoryCheckBehavior
          isSerialized
          stockUnit {
            id
            name
            unitType
            baseUnit {
              name
              id
            }
          }
          purchaseUnit {
            id
            name
            unitType
            baseUnit {
              name
            }
          }
          procurementRecords {
            procurementVendor {
              id
            }
            warehouse {
              id
            }
            purchasePrice {
              amount
            }
          }
          warehouseAssociation(warehouseIds: $warehouseIds) {
            orderability
            customOrderabilityMessage
            standingOrderBehavior
            inventoryCheckBehavior
            procurementVendor {
              id
            }
            salePrice {
              cents
              currency
            }
            warehouse {
              id
            }
            sample {
              id
              name
            }
            hasSample
            customerFacingOutOfStock
            outOfStockSince
            configuredFulfillmentWarehouses {
              warehouse {
                id
                name
              }
            }
          }
          sampleUnit {
            name
            id
          }
          sampleUpcCodes
        }
        ... on NonInventoryItem {
          marketPrice
          markupPercentage
          minItemsPerOrder
          purchaseMultiple
          discontinuedOn
          warehouseAssociation(warehouseIds: $warehouseIds) {
            orderability
            customOrderabilityMessage
            standingOrderBehavior
            vendor {
              id
            }
            salePrice {
              cents
              currency
            }
            warehouse {
              id
            }
            sample {
              id
              name
            }
            hasSample
            customerFacingOutOfStock
            outOfStockSince
            configuredFulfillmentWarehouses {
              warehouse {
                id
                name
              }
            }
          }
          sampleUnit {
            name
            id
          }
        }
        purchasePrice {
          amount
          cents
          currency
        }
        saleUnit {
          id
          unitType
          name
          conversionRate
        }
      }
    }
  }
`;

gql`
  query getSignedUploadImageByKey($key: String!) {
    signedUploadImageByKey(key: $key) {
      url
    }
  }
`;

gql`
  query getSignedUploadImageUrl($extension: String!) {
    signedUploadImageUrl(extension: $extension) {
      url
      key
    }
  }
`;

gql`
  query getCategoriesForWarehouse($warehouseId: ID!) {
    warehouse(warehouseId: $warehouseId) {
      categories {
        parentCategory {
          id
        }
        id
        name
        position
        subcategories {
          id
          name
          position
        }
      }
    }
  }
`;

export const GET_WAREHOUSES = gql`
  query getWarehouses {
    warehouses {
      id
      name
    }
  }
`;

export const GET_WAREHOUSE_LOCATIONS = gql`
  query getWarehouseLocations($warehouseId: ID!) {
    warehouse(warehouseId: $warehouseId) {
      locations {
        id
      }
    }
  }
`;

export const GET_WAREHOUSES_WITH_LEAD_TIME = gql`
  query getWarehousesWithLeadTime {
    warehouses {
      id
      name
      brandedPaperLeadTime
    }
  }
`;

export const GET_PROCUREMENT_VENDOR_IDS = gql`
  query getProcurementVendors {
    procurementVendors {
      id
    }
  }
`;

gql`
  query findItemReadOnly(
    $itemId: ID!
    $warehouseIds: [ID!]
    $withDiscardedAtItemTags: Boolean
  ) {
    viewer {
      availableItemById(id: $itemId) {
        __typename
        id
        name
        description
        catalogStatus
        inactivatedOn
        createdAt
        createdBy
        imgUrl
        updatedAt
        vendorCode
        brandName
        brand {
          id
          name
        }
        category {
          name
        }
        subcategory {
          name
        }
        standingOrderBehavior
        maxQuantityPerLocation
        sampleMaxQuantityPerLocation
        refrigerationState
        transitMethod
        dimensionalFactor
        eachHeightInches
        eachLengthInches
        eachWeightLbs
        eachWidthInches
        caseHeightInches
        caseLengthInches
        caseWeightLbs
        caseWidthInches
        casesPerLayer
        casesPerPallet
        palletWeightLbs
        dimensioned
        nmfc
        layersPerPallet
        itemSize
        size {
          label
        }
        tags(withDiscardedAt: $withDiscardedAtItemTags) {
          badgeUrl
          displayAs
          id
          name
          parentTag {
            badgeUrl
            displayAs
            id
            name
            position
          }
          pivot {
            itemId
            discardedAt
          }
        }
        ... on InventoryItem {
          manufacturedType
          marketPrice
          perishability
          upcCodes
          locationShelfLifeDays
          shelfLifeDays
          inventoryCheckBehavior
          isSerialized
          stockUnit {
            name
            unitType
            baseUnit {
              name
            }
          }
          purchaseUnit {
            name
            unitType
            baseUnit {
              name
            }
          }
          procurementRecords {
            procurementVendor {
              id
            }
            warehouse {
              id
            }
            purchasePrice {
              amount
            }
          }
          warehouseAssociation(warehouseIds: $warehouseIds) {
            procurementVendor {
              id
              name
            }
            salePrice {
              cents
              currency
            }
            warehouse {
              id
              name
            }
            sample {
              id
              name
            }
            hasSample
            orderability
            standingOrderBehavior
            inventoryCheckBehavior
            customerFacingOutOfStock
            outOfStockSince
            configuredFulfillmentWarehouses {
              warehouse {
                id
                name
              }
            }
          }
          sampleUnit {
            name
            id
          }
          sampleUpcCodes
          erpRecordUrl
        }
        ... on NonInventoryItem {
          marketPrice
          markupPercentage
          minItemsPerOrder
          purchaseMultiple
          warehouseAssociation(warehouseIds: $warehouseIds) {
            vendor {
              id
              name
              legalName
              warehouse {
                name
              }
            }
            salePrice {
              cents
              currency
            }
            warehouse {
              id
              name
            }
            sample {
              id
              name
            }
            hasSample
            orderability
            standingOrderBehavior
            customerFacingOutOfStock
            outOfStockSince
            configuredFulfillmentWarehouses {
              warehouse {
                id
                name
              }
            }
          }
          sampleUnit {
            name
            id
          }
          erpRecordUrl
        }
        purchasePrice {
          amount
          cents
          currency
        }
        saleUnit {
          unitType
          name
          conversionRate
        }
      }
    }
    productInventory(productId: $itemId) {
      availableCount
      warehouse {
        id
        name
      }
    }
  }
`;

gql`
  query IndexTableItems(
    $first: Int
    $after: String
    $filters: ItemsFiltersInput
    $sortOrder: [ItemsSortOrder]
    $searchQuery: String
  ) {
    viewer {
      availableItems(
        first: $first
        after: $after
        filters: $filters
        sortOrder: $sortOrder
        searchQuery: $searchQuery
      ) {
        edges {
          node {
            name
            id
            name
            brandName
            brand {
              id
              name
            }
            catalogStatus
            category {
              name
              id
              position
            }
            imgUrl
            saleUnit {
              name
            }
            updatedAt
          }
          cursor
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

gql`
  query IndexTableBrands(
    $first: Int
    $after: String
    $filters: ItemBrandsFiltersInput
    $sortOrder: [ItemBrandsSortOrder]
  ) {
    itemBrands(
      first: $first
      after: $after
      filters: $filters
      sortOrder: $sortOrder
    ) {
      edges {
        node {
          name
          id
        }
        cursor
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const FIND_BRAND_BY_NAME = gql`
  query FindBrandByName(
    $first: Int
    $after: String
    $filters: ItemBrandsFiltersInput
    $sortOrder: [ItemBrandsSortOrder]
  ) {
    itemBrands(
      first: $first
      after: $after
      filters: $filters
      sortOrder: $sortOrder
    ) {
      edges {
        node {
          name
          id
        }
        cursor
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_ITEM_TAGS = gql`
  query ItemTags(
    $first: Int
    $after: String
    $filters: ItemTagFiltersInput
    $sortOrder: [ItemTagSortOrder]
    $searchQuery: String
  ) {
    viewer {
      itemTags(
        first: $first
        after: $after
        filters: $filters
        sortOrder: $sortOrder
        searchQuery: $searchQuery
      ) {
        edges {
          node {
            id
            name
            badgeUrl
            position
            displayAs
            parentTag {
              id
              name
              position
            }
          }
          cursor
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

gql`
  query AuditLogsForObject(
    $after: String
    $before: String
    $endDate: ISO8601DateTime
    $excludeObjectTypes: [String!]
    $first: Int
    $itemId: ID
    $last: Int
    $objects: [AuditLogObjectInput]
    $sortOrder: [AuditLogSortOrder]
    $startDate: ISO8601DateTime
  ) {
    viewer {
      auditLogs(
        after: $after
        before: $before
        endDate: $endDate
        excludeObjectTypes: $excludeObjectTypes
        first: $first
        itemId: $itemId
        last: $last
        objects: $objects
        sortOrder: $sortOrder
        startDate: $startDate
      ) {
        edges {
          cursor
          node {
            causedBy
            changeType
            date
            field
            itemId
            newValue
            objectId
            oldValue
            objectType
            user {
              email
              id
              name
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
  }
`;

gql`
  query LocationChangeGroups(
    $after: String
    $before: String
    $first: Int
    $itemId: ID!
    $last: Int
  ) {
    viewer {
      locationChangeGroups(
        after: $after
        before: $before
        first: $first
        itemId: $itemId
        last: $last
      ) {
        edges {
          cursor
          node {
            changeType
            endedAt
            locationIds
            startedAt
            user {
              email
              id
              name
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
  }
`;

export const GET_LANDED_COST_FOR_ITEM = gql`
  query getLandedCostForItem($itemId: ID!, $warehouseIds: [ID!]) {
    viewer {
      availableItemById(id: $itemId) {
        name
        purchasePrice {
          amount
          cents
          currency
        }
        ... on InventoryItem {
          warehouseAssociation(warehouseIds: $warehouseIds) {
            landedCost {
              cents
              currency
            }
          }
        }
        ... on NonInventoryItem {
          warehouseAssociation(warehouseIds: $warehouseIds) {
            landedCost {
              cents
              currency
            }
          }
        }
      }
    }
  }
`;

export const GET_ITEM_NAME_BY_UUID = gql`
  query getItemNameByUUID($itemId: ID!) {
    viewer {
      availableItemById(id: $itemId) {
        name
        catalogStatus
      }
    }
  }
`;

export const GET_LOCATION_NAME_BY_UUID = gql`
  query Location($odekoUuid: ID) {
    location(odekoUuid: $odekoUuid) {
      businessName
    }
  }
`;

export const QUERY_LOCATION_ITEMS_BY_ITEM_ID = gql`
  query LocationItemsByItemId($locationItemsByItemId: ID!) {
    locationItemsByItemId(id: $locationItemsByItemId) {
      locationId
    }
  }
`;

export const QUERY_LOCATIONS_SEARCH = gql`
  query locationsSearch($first: Int!, $name: String!) {
    viewer {
      locations(first: $first, name: $name) {
        nodes {
          id
          businessName
          warehouse {
            id
            name
          }
        }
      }
    }
  }
`;

export const CREATE_COLLECTION_PROMO_SETUP = gql`
  mutation createCollectionPromoSetup(
    $input: CreateCollectionPromoSetupInput!
  ) {
    createCollectionPromoSetup(input: $input) {
      collectionId
      promoPageId
      errors {
        field
        message
      }
    }
  }
`;

export const GET_COLLECTION = gql`
  query getCollection($id: ID, $referenceId: String, $type: String) {
    collection(id: $id, referenceId: $referenceId, type: $type) {
      id
      name
      description
      promoName
      slug
      type
      aggregation
      reference
      referenceId
    }
  }
`;

export const GET_COLLECTION_ITEMS = gql`
  query getCollectionItems(
    $first: Int
    $after: String
    $filters: ItemsFiltersInput
  ) {
    viewer {
      availableItems(first: $first, after: $after, filters: $filters) {
        edges {
          node {
            __typename
            id
            name
            brandName
            imgUrl
            ... on InventoryItem {
              warehouseAssociation {
                warehouse {
                  name
                }
              }
            }
            ... on NonInventoryItem {
              warehouseAssociation {
                warehouse {
                  name
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const GET_COLLECTION_SETUP_FOR_EDIT = gql`
  query getCollectionSetupForEdit($collectionId: ID!) {
    collectionSetupForEdit(collectionId: $collectionId) {
      collection {
        id
        name
        description
        promoName
        slug
      }
      itemIds
      promoPageWarehouses {
        warehouse {
          id
          name
        }
        desktopHeroImage
        mobileHeroImage
      }
      homepageComponents {
        componentId
        componentType
        warehouseAttributes {
          warehouse {
            id
            name
          }
          desktopImage
          mobileImage
          image
          fontStyle
          enabled
        }
      }
      displayRules {
        ruleId
        componentId
        componentType
        position
        startDate
        endDate
        warehouseIds
      }
    }
  }
`;

export const GET_CATALOG_HOMEPAGE_ROWS = gql`
  query getCatalogHomepageRows($date: ISO8601Date) {
    catalogHomepageRows(date: $date) {
      position
      activeNow {
        ruleId
        collection {
          id
          name
        }
        startDate
        endDate
        enabledWarehouseCount
        configuredWarehouseCount
        warehouseIds
        warehouses {
          id
          name
        }
      }
      scheduled {
        ruleId
        collection {
          id
          name
        }
        startDate
        endDate
        enabledWarehouseCount
        configuredWarehouseCount
        warehouseIds
        warehouses {
          id
          name
        }
      }
    }
  }
`;

export const GET_PAST_HOMEPAGE_COLLECTIONS = gql`
  query getPastHomepageCollections($date: ISO8601Date) {
    pastHomepageCollections(date: $date) {
      ruleId
      collection {
        id
        name
      }
      componentType
      startDate
      endDate
      enabledWarehouseCount
      configuredWarehouseCount
      warehouseIds
      warehouses {
        id
        name
      }
    }
  }
`;

export const GET_MASTER_HOMEPAGES = gql`
  query getMasterHomepages {
    masterHomepages {
      id
      name
      startDate
      endDate
    }
  }
`;

export const GET_MASTER_HOMEPAGE_FOR_EDIT = gql`
  query getMasterHomepageForEdit($id: ID!) {
    masterHomepageForEdit(id: $id) {
      id
      name
      startDate
      endDate
      rows {
        id
        sortOrder
        structureType
        components {
          id
          catalogComponentId
          collectionId
          componentType
          sortOrder
          startDate
          endDate
          warehouseIds
          desktopImage
          mobileImage
          fontStyle
          image
        }
      }
    }
  }
`;

export const GET_COLLECTIONS_PAGE = gql`
  query getCollectionsPage {
    collectionsPage {
      collection {
        id
        name
        slug
      }
      configuredWarehouseCount
      warehouseIds
      warehouses {
        id
        name
      }
    }
  }
`;

gql`
  query ItemIssuesTableItems(
    $first: Int
    $after: String
    $filters: ItemsFiltersInput
    $sortOrder: [ItemsSortOrder]
    $searchQuery: String
  ) {
    viewer {
      availableItems(
        first: $first
        after: $after
        filters: $filters
        sortOrder: $sortOrder
        searchQuery: $searchQuery
      ) {
        edges {
          node {
            __typename
            id
            name
            brand {
              id
              name
            }
            description
            hasOwnImage
            imgUrl
            category {
              name
              id
            }
            updatedAt
            ... on InventoryItem {
              upcCodes
              warehouseAssociation {
                warehouse {
                  id
                  name
                }
              }
            }
            ... on NonInventoryItem {
              warehouseAssociation {
                warehouse {
                  id
                  name
                }
              }
            }
          }
          cursor
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        totalCount
      }
    }
  }
`;

gql`
  query itemOosSubstitutes($itemId: ID!, $limit: Int) {
    viewer {
      availableItemById(id: $itemId) {
        id
        substitutionPreview(limit: $limit) {
          id
          name
          brandName
          imgUrl
        }
        substitutionOverrides {
          id
          source
          substituteItem {
            id
          }
        }
      }
    }
  }
`;

gql`
  query itemSubstitutesManagement($itemId: ID!, $warehouseId: ID, $limit: Int) {
    viewer {
      availableItemById(id: $itemId) {
        id
        name
        substitutionPreview(warehouseId: $warehouseId, limit: $limit) {
          id
          name
          brandName
          imgUrl
        }
        substitutionOverrides {
          id
          priority
          reason
          source
          substituteItem {
            id
            name
            brandName
            imgUrl
          }
        }
      }
    }
  }
`;

export const GET_CATEGORY_TREE = gql`
  query getCategoryTree {
    categories(filter: TOP_LEVEL) {
      id
      name
      position
      activeWarehouses {
        id
        name
      }
      bannerWarehouses {
        id
        name
      }
      hasBannerDefaultCreative
      subcategories {
        id
        name
        position
        activeWarehouses {
          id
          name
        }
        bannerWarehouses {
          id
          name
        }
        hasBannerDefaultCreative
      }
    }
  }
`;

export const GET_CATEGORY_BANNER_FOR_EDIT = gql`
  query getCategoryBannerForEdit($categoryId: ID!) {
    categoryBannerForEdit(categoryId: $categoryId) {
      categoryBannerId
      category {
        id
        name
        activeWarehouses {
          id
          name
        }
        subcategories {
          id
          name
        }
      }
      defaultCreative {
        desktopImage
        mobileImage
        shippingBehavior
        shippingDesktopImage
        shippingMobileImage
        destinationType
        destinationRef
      }
      warehouses {
        warehouse {
          id
          name
        }
        hidden
        desktopImage
        mobileImage
        shippingBehavior
        shippingDesktopImage
        shippingMobileImage
        destinationType
        destinationRef
      }
    }
  }
`;
